import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Wine, Layers, Trophy, Users, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "fs_just_joined_venue";
const WELCOME_TTL_MS = 5 * 60 * 1000; // Honour the flag for 5 minutes after redemption

/**
 * WelcomeToVenue
 * Full-screen overlay that fires the first time a user lands on the dashboard
 * after redeeming an invite code. Shows what they've just unlocked (cocktails,
 * decks, leaderboard, teammates) — turns the join moment into something memorable.
 *
 * To trigger: caller sets localStorage[STORAGE_KEY] = JSON.stringify({
 *   venue_name, org_name, ts: Date.now()
 * })
 * The overlay self-clears the flag once dismissed.
 */
export default function WelcomeToVenue() {
  const [show, setShow] = useState(false);
  const [payload, setPayload] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let raw;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { localStorage.removeItem(STORAGE_KEY); return; }
    if (!parsed?.ts || Date.now() - parsed.ts > WELCOME_TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    setPayload(parsed);
    setShow(true);

    // Fetch what they've unlocked — best effort, fail silently
    (async () => {
      try {
        const [cocktailsRes, decksRes, staffRes] = await Promise.all([
          axios.get(`${API}/cocktails`).catch(() => ({ data: [] })),
          axios.get(`${API}/decks`).catch(() => ({ data: [] })),
          axios.get(`${API}/org/staff`).catch(() => ({ data: [] })),
        ]);
        setStats({
          cocktails: (cocktailsRes.data || []).length,
          decks: (decksRes.data || []).length,
          teammates: Math.max(0, (staffRes.data || []).length - 1),
        });
      } catch {
        setStats({ cocktails: 0, decks: 0, teammates: 0 });
      }
    })();
  }, []);

  const dismiss = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {
      // localStorage might be disabled — overlay still closes via state
    }
    setShow(false);
  };

  if (!show || !payload) return null;

  const venue = payload.venue_name || "your team";
  const org = payload.org_name;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="welcome-overlay" role="dialog" aria-label={`Welcome to ${venue}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-fs-fade-in" onClick={dismiss} />

      {/* Card */}
      <div className="relative w-full max-w-lg rounded-3xl overflow-hidden border border-[hsl(var(--primary))]/40 shadow-[0_30px_80px_-20px_rgba(0,0,0,.7),0_0_60px_-15px_hsl(var(--primary)/.4)] animate-fs-pop-in">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--primary))]/15 via-[hsl(var(--card))] to-[hsl(var(--card))] pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[hsl(var(--primary))]/15 blur-3xl pointer-events-none" />

        <button onClick={dismiss} className="absolute top-4 right-4 z-10 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] p-1" aria-label="Close" data-testid="welcome-close">
          <X size={18} />
        </button>

        <div className="relative p-8 sm:p-10 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(var(--primary))]/15 border border-[hsl(var(--primary))]/30 text-[10px] uppercase tracking-[.2em] font-bold text-[hsl(var(--primary))] mb-5" data-testid="welcome-pill">
            <Sparkles size={11} /> You're in
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Playfair Display, Georgia, serif" }} data-testid="welcome-headline">
            Welcome to <span className="text-[hsl(var(--primary))] italic">{venue}</span>
          </h2>
          {org && (
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">part of <span className="text-[hsl(var(--foreground))] font-medium">{org}</span></p>
          )}

          <div className="grid grid-cols-3 gap-3 mt-8" data-testid="welcome-stats">
            <UnlockTile icon={Wine} value={stats?.cocktails ?? "·"} label="Cocktails" />
            <UnlockTile icon={Layers} value={stats?.decks ?? "·"} label="Decks" />
            <UnlockTile icon={Users} value={stats?.teammates ?? "·"} label="Teammates" />
          </div>

          <ul className="mt-7 space-y-2 text-left text-sm">
            <li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
              <Trophy size={14} className="text-[hsl(var(--primary))] flex-shrink-0" />
              Weekly leaderboard now tracks your XP
            </li>
            <li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
              <Layers size={14} className="text-[hsl(var(--primary))] flex-shrink-0" />
              Beautiful flashcards for your venue's full menu
            </li>
            <li className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
              <Sparkles size={14} className="text-[hsl(var(--primary))] flex-shrink-0" />
              Targeted practice on your weak areas
            </li>
          </ul>

          <Button
            onClick={dismiss}
            className="w-full mt-8 bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90 font-bold h-12 text-base"
            data-testid="welcome-go"
          >
            Let's go <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes fsFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fsPopIn { from { opacity: 0; transform: scale(.92) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .animate-fs-fade-in { animation: fsFadeIn .25s ease-out; }
        .animate-fs-pop-in { animation: fsPopIn .42s cubic-bezier(.16,1,.3,1); }
      `}</style>
    </div>
  );
}

function UnlockTile({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-4 flex flex-col items-center justify-center gap-1">
      <Icon size={18} className="text-[hsl(var(--primary))]" />
      <p className="text-2xl font-bold text-[hsl(var(--foreground))] leading-none mt-1" style={{ fontFamily: "Playfair Display, Georgia, serif" }}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-semibold">{label}</p>
    </div>
  );
}
